import { useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import { DataGrid } from "@mui/x-data-grid";
import { useTranslation } from "react-i18next";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Divider from "@mui/material/Divider";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import { getNode, getOnlineNodes as getOnlineNodesAPI } from "../../api/nodeAPI";
import { getDevices } from "../../api/deviceAPI";
import { renderOnline, renderOffline } from "../../components/StatusChip";
import {
  getNodeSlotDefinition,
  postNodeSlotDefinition,
  putNodeSlotDefinition,
  deleteNodeSlotDefinition,
} from "../../api/nodeSlotDefinitionAPI";
import {getSlotsDefinitions} from "../../api/slotsDefinitionsAPI";

const ColumnHeaderWithFilter = ({ headerName, field, filters, setFilters }) => {
  return (
    <Box
      sx={{ display: "flex", flexDirection: "column", width: "100%", gap: 0.5 }}
    >
      <Typography
        variant="subtitle2"
        sx={{ fontWeight: 600, textAlign: "center" }}
      >
        {headerName}
      </Typography>
      <TextField
        size="small"
        placeholder="Ara..."
        value={filters[field] || ""}
        onChange={(e) => {
          e.stopPropagation();
          setFilters((prev) => ({ ...prev, [field]: e.target.value }));
        }}
        onClick={(e) => e.stopPropagation()}
        sx={{
          "& .MuiInputBase-root": {
            fontSize: "0.75rem",
            minHeight: "30px",
          },
          "& .MuiInputBase-input": {
            padding: "4px 8px",
          },
        }}
      />
    </Box>
  );
};

const StatusHeaderWithFilter = ({ headerName, filters, setFilters, t }) => {
  return (
    <Box
      sx={{ display: "flex", flexDirection: "column", width: "100%", gap: 0.5 }}
    >
      <Typography
        variant="subtitle2"
        sx={{ fontWeight: 600, textAlign: "center" }}
      >
        {headerName}
      </Typography>
      <FormControl size="small" fullWidth>
        <Select
          value={filters["status"] || ""}
          onChange={(e) => {
            e.stopPropagation();
            setFilters((prev) => ({ ...prev, status: e.target.value }));
          }}
          onClick={(e) => e.stopPropagation()}
          displayEmpty
          sx={{
            fontSize: "0.75rem",
            minHeight: "30px",
            "& .MuiSelect-select": {
              padding: "4px 8px",
            },
          }}
        >
          <MenuItem value="">{t("common.all") || "Tümü"}</MenuItem>
          <MenuItem value="Online">{t("places.online") || "Çevrimiçi"}</MenuItem>
          <MenuItem value="Offline">{t("places.offline") || "Çevrimdışı"}</MenuItem>
        </Select>
      </FormControl>
    </Box>
  );
};

export const getColumns = (t, onlineIds, filters, setFilters) => {
  return [
    {
      field: "idNode",
      headerName: t("places.dataGrid.id"),
      flex: 0.5,
      minWidth: 80,
      headerAlign: "center",
      align: "center",
      renderHeader: () => (
        <ColumnHeaderWithFilter
          headerName={t("places.dataGrid.id")}
          field="idNode"
          filters={filters}
          setFilters={setFilters}
        />
      ),
    },
    {
      field: "status",
      headerName: t("places.dataGrid.status"),
      flex: 1,
      minWidth: 100,
      headerAlign: "center",
      align: "center",
      renderCell: (params) =>
        onlineIds?.includes(params.row.idNode)
          ? renderOnline("Online", t)
          : renderOffline("Offline", t),
      renderHeader: () => (
        <StatusHeaderWithFilter
          headerName={t("places.dataGrid.status")}
          filters={filters}
          setFilters={setFilters}
          t={t}
        />
      ),
    },
    {
      field: "code",
      headerName: t("places.dataGrid.code"),
      flex: 2,
      minWidth: 120,
      headerAlign: "center",
      align: "center",
      renderHeader: () => (
        <ColumnHeaderWithFilter
          headerName={t("places.dataGrid.code")}
          field="code"
          filters={filters}
          setFilters={setFilters}
        />
      ),
    },
    {
      field: "name",
      headerName: t("places.dataGrid.name"),
      flex: 2,
      minWidth: 150,
      headerAlign: "center",
      align: "center",
      renderHeader: () => (
        <ColumnHeaderWithFilter
          headerName={t("places.dataGrid.name")}
          field="name"
          filters={filters}
          setFilters={setFilters}
        />
      ),
    },
    {
      field: "slotDefinitionTable",
      headerName: t("places.dataGrid.slotDefinitionTable"),
      flex: 1,
      minWidth: 150,
      headerAlign: "center",
      align: "center",
      renderHeader: () => (
        <ColumnHeaderWithFilter
          headerName={t("places.dataGrid.slotDefinitionTable")}
          field="slotDefinitionTable"
          filters={filters}
          setFilters={setFilters}
        />
      ),
    },
    {
      field: "terminalName",
      headerName: t("places.dataGrid.terminalName"),
      flex: 1,
      minWidth: 150,
      headerAlign: "center",
      align: "center",
      renderHeader: () => (
        <ColumnHeaderWithFilter
          headerName={t("places.dataGrid.terminalName")}
          field="terminalName"
          filters={filters}
          setFilters={setFilters}
        />
      ),
    },
    {
      field: "address",
      headerName: t("places.dataGrid.address"),
      flex: 1,
      minWidth: 200,
      headerAlign: "center",
      align: "center",
      renderHeader: () => (
        <ColumnHeaderWithFilter
          headerName={t("places.dataGrid.address")}
          field="address"
          filters={filters}
          setFilters={setFilters}
        />
      ),
    },
    {
      field: "city",
      headerName: t("places.dataGrid.city"),
      flex: 1,
      minWidth: 120,
      headerAlign: "center",
      align: "center",
      renderHeader: () => (
        <ColumnHeaderWithFilter
          headerName={t("places.dataGrid.city")}
          field="city"
          filters={filters}
          setFilters={setFilters}
        />
      ),
    },
    {
      field: "town",
      headerName: t("places.dataGrid.town"),
      flex: 1,
      minWidth: 120,
      headerAlign: "center",
      align: "center",
      renderHeader: () => (
        <ColumnHeaderWithFilter
          headerName={t("places.dataGrid.town")}
          field="town"
          filters={filters}
          setFilters={setFilters}
        />
      ),
    },
    {
      field: "group1",
      headerName: t("places.dataGrid.group1"),
      flex: 1,
      minWidth: 100,
      headerAlign: "center",
      align: "center",
      renderHeader: () => (
        <ColumnHeaderWithFilter
          headerName={t("places.dataGrid.group1")}
          field="group1"
          filters={filters}
          setFilters={setFilters}
        />
      ),
    },
    {
      field: "group2",
      headerName: t("places.dataGrid.group2"),
      flex: 1,
      minWidth: 100,
      headerAlign: "center",
      align: "center",
      renderHeader: () => (
        <ColumnHeaderWithFilter
          headerName={t("places.dataGrid.group2")}
          field="group2"
          filters={filters}
          setFilters={setFilters}
        />
      ),
    },
    {
      field: "group3",
      headerName: t("places.dataGrid.group3"),
      flex: 1,
      minWidth: 100,
      headerAlign: "center",
      align: "center",
      renderHeader: () => (
        <ColumnHeaderWithFilter
          headerName={t("places.dataGrid.group3")}
          field="group3"
          filters={filters}
          setFilters={setFilters}
        />
      ),
    },
  ];
};

export default function NodeSlotGrid() {
  const { t } = useTranslation();
  const [nodes, setNodes] = useState([]);
  const [onlineNodeIds, setOnlineNodeIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [devices, setDevices] = useState([]);
  const [slotDefinitions, setSlotDefinitions] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [slotDefinitionsList, setSlotDefinitionsList] = useState([]);
  const [selectedSlotDefinition, setSelectedSlotDefinition] = useState("");
  const [columnFilters, setColumnFilters] = useState({});
  const [isLoadingSlots, setIsLoadingSlots] = useState(false);
  const [isRemovingSlots, setIsRemovingSlots] = useState(false);
  const [toast, setToast] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [openLoadConfirmDialog, setOpenLoadConfirmDialog] = useState(false);
  const [openRemoveConfirmDialog, setOpenRemoveConfirmDialog] = useState(false);
  const [showInfoBox, setShowInfoBox] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const [nodesResult, onlineNodesResult, devicesResponse, slotDefs, slotDefList] = await Promise.all([
          getNode(),
          getOnlineNodesAPI(),
          getDevices(),
          getNodeSlotDefinition(),
          getSlotsDefinitions(),
        ]);
        console.log("📊 Loaded NodeSlotDefinitions:", slotDefs);
        console.log("📋 Sample slot def:", slotDefs?.[0]);
        setNodes(nodesResult || []);
        const ids = (onlineNodesResult || []).map((node) => node.idNode);
        setOnlineNodeIds(ids);
        setDevices(devicesResponse?.data || devicesResponse || []);
        setSlotDefinitions(slotDefs || []);
        setSlotDefinitionsList(slotDefList || []);
      } catch (error) {
        console.error("Error loading data:", error);
        setDevices([]);
        setSlotDefinitions([]);
        setSlotDefinitionsList([]);
      } finally {
        setLoading(false);
      }
    };

    loadData();

    const interval = setInterval(async () => {
      try {
        const result = await getOnlineNodesAPI();
        const ids = (result || []).map((node) => node.idNode);
        setOnlineNodeIds(ids);
      } catch (err) {
        console.error("Error fetching online nodes:", err);
      }
    }, 300000);

    return () => clearInterval(interval);
  }, []);

  const onlineIds = useMemo(() => onlineNodeIds, [onlineNodeIds]);

  const rows = useMemo(() => {
    const allRows = nodes.map((node) => {
      const slotDef = slotDefinitions.find(
        (sd) => Number(sd.nodeId || sd.node_idNode) === Number(node.idNode)
      );

      return {
        id: node.idNode,
        idNode: node.idNode,
        status: onlineIds.includes(node.idNode) ? "Online" : "Offline",
        name: node.name,
        code: node.code,
        regionName: node.regionName || "Bölge Yok",
        address: node.address,
        city: node.city,
        town: node.town,
        group1: node.group1,
        group2: node.group2,
        group3: node.group3,
        terminalName:
          (devices || [])
            .filter((d) => Number(d.node_idNode) === Number(node.idNode))
            .map(
              (d) => d.nameDevice || d.serialDevice || `Device ${d.idDevice}`
            )
            .join(", ") || "",
        slotDefinitionTable: slotDef?.slotDefinitionTable_name || "",
      };
    });

    // Kolon bazlı filtreleme
    const hasActiveFilters = Object.values(columnFilters).some(
      (filter) => filter
    );
    if (!hasActiveFilters) return allRows;

    return allRows.filter((row) => {
      return Object.entries(columnFilters).every(([field, filterValue]) => {
        if (!filterValue) return true; // Boş filtreler her şeyi geçirir

        const cellValue = row[field];
        if (cellValue == null) return false;

        return cellValue
          .toString()
          .toLowerCase()
          .includes(filterValue.toLowerCase());
      });
    });
  }, [nodes, onlineIds, devices, slotDefinitions, columnFilters]);

  const handleLoadSlotDefinitionClick = () => {
    if (!selectedSlotDefinition || selectedRows.length === 0) {
      return;
    }
    setOpenLoadConfirmDialog(true);
  };

  const handleLoadSlotDefinitionConfirm = async () => {
    setOpenLoadConfirmDialog(false);
    setIsLoadingSlots(true);

    try {
      // Seçili rowların detaylarını bul
      const selectedNodesData = rows.filter((row) =>
        selectedRows.includes(row.idNode)
      );

      // Her bir seçili row için sırayla işlem yap
      for (const nodeData of selectedNodesData) {
        const nodeId = nodeData.idNode;

        // Bu node'un mevcut slot definition'ını kontrol et
        const existingSlotDef = slotDefinitions.find(
          (sd) => Number(sd.nodeId || sd.node_idNode) === Number(nodeId)
        );

        try {
          if (existingSlotDef) {
            // Slot definition varsa PUT yap
            console.log(
              `Updating slot definition for node ${nodeId}:`,
              existingSlotDef.idnodeSlotDefinition
            );
            await putNodeSlotDefinition(
              existingSlotDef.idnodeSlotDefinition,
              nodeId,
              selectedSlotDefinition
            );
            console.log(`✓ Updated node ${nodeId}`);
          } else {
            // Slot definition yoksa POST yap
            console.log(`Creating slot definition for node ${nodeId}`);
            await postNodeSlotDefinition(nodeId, selectedSlotDefinition);
            console.log(`✓ Created node ${nodeId}`);
          }
        } catch (error) {
          console.error(`✗ Failed for node ${nodeId}:`, error);
          // Hata olsa bile devam et
        }
      }

      // Tüm işlemler bittikten sonra verileri yeniden yükle
      const slotDefs = await getNodeSlotDefinition();
      setSlotDefinitions(slotDefs || []);

      setToast({
        open: true,
        message:
          t("stocks.nodeSlotGrid.slotDefinitionLoadedSuccess") ||
          "Slot tanımları başarıyla yüklendi!",
        severity: "success",
      });
    } catch (error) {
      console.error("Error loading slot definitions:", error);
      setToast({
        open: true,
        message:
          t("stocks.nodeSlotGrid.slotDefinitionLoadedError") ||
          "Slot tanımları yüklenirken hata oluştu!",
        severity: "error",
      });
    } finally {
      setIsLoadingSlots(false);
    }
  };

  const handleRemoveSlotDefinitionClick = () => {
    if (selectedRows.length === 0) {
      return;
    }
    setOpenRemoveConfirmDialog(true);
  };

  const handleRemoveSlotDefinitionConfirm = async () => {
    setOpenRemoveConfirmDialog(false);
    setIsRemovingSlots(true);

    try {
      // Seçili rowların detaylarını bul
      const selectedNodesData = rows.filter((row) =>
        selectedRows.includes(row.idNode)
      );

      let successCount = 0;
      let failCount = 0;

      // Her bir seçili row için sırayla işlem yap
      for (const nodeData of selectedNodesData) {
        const nodeId = nodeData.idNode;

        // Bu node'un mevcut slot definition'ını kontrol et
        const existingSlotDef = slotDefinitions.find(
          (sd) => Number(sd.nodeId || sd.node_idNode) === Number(nodeId)
        );

        if (!existingSlotDef) {
          console.log(`⊘ Node ${nodeId} has no slot definition to remove`);
          continue;
        }

        try {
          // Slot definition varsa DELETE yap
          console.log(
            `Removing slot definition for node ${nodeId}:`,
            existingSlotDef.idnodeSlotDefinition
          );

          await deleteNodeSlotDefinition(existingSlotDef.idnodeSlotDefinition);

          // returnCode 0 ise başarılı (exception fırlatmazsa başarılı demektir)
          console.log(`✓ Successfully removed from node ${nodeId}`);
          successCount++;
        } catch (error) {
          console.error(`✗ Failed to remove from node ${nodeId}:`, error);
          failCount++;
          // Hata olsa bile devam et
        }
      }

      // Tüm işlemler bittikten sonra verileri yeniden yükle
      const slotDefs = await getNodeSlotDefinition();
      setSlotDefinitions(slotDefs || []);

      console.log(
        `✓ Removal complete: ${successCount} successful, ${failCount} failed`
      );

      if (successCount > 0) {
        setToast({
          open: true,
          message:
            t("stocks.nodeSlotGrid.slotDefinitionRemovedSuccess") ||
            "Slot tanımları başarıyla kaldırıldı!",
          severity: "success",
        });
      } else {
        setToast({
          open: true,
          message:
            t("stocks.nodeSlotGrid.slotDefinitionRemovedError") ||
            "Slot tanımları kaldırılırken hata oluştu!",
          severity: "error",
        });
      }
    } catch (error) {
      console.error("Error removing slot definitions:", error);
      setToast({
        open: true,
        message:
          t("stocks.nodeSlotGrid.slotDefinitionRemovedError") ||
          "Slot tanımları kaldırılırken hata oluştu!",
        severity: "error",
      });
    } finally {
      setIsRemovingSlots(false);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        height: "100%",
        minHeight: { xs: 350, md: 600 },
      }}
    >
      {/* Usage Instructions */}
      {showInfoBox && (
        <Box
          sx={{
            p: 2,
            backgroundColor: "info.light",
            borderRadius: 1,
            border: "1px solid",
            borderColor: "info.main",
            position: "relative",
          }}
        >
          <IconButton
            size="small"
            onClick={() => setShowInfoBox(false)}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: "info.dark",
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
          <Typography variant="body2" sx={{ fontWeight: "bold", mb: 1, pr: 4 }}>
            {t("stocks.nodeSlotGrid.howToUseTitle")}
          </Typography>
          <Typography variant="body2">
            {t("stocks.nodeSlotGrid.howToUseDescription")}
          </Typography>
        </Box>
      )}

      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 1.5,
          width: "100%",
          px: { xs: 1, sm: 0 },
        }}
      >
        <FormControl sx={{ width: { xs: "100%", sm: 300 } }} size="small">
          <InputLabel id="slot-definition-select-label">
            {t("stocks.nodeSlotGrid.selectSlotDefinition") ||
              "Slot Tanımı Seçin"}
          </InputLabel>
          <Select
            labelId="slot-definition-select-label"
            value={selectedSlotDefinition}
            label={
              t("stocks.nodeSlotGrid.selectSlotDefinition") ||
              "Slot Tanımı Seçin"
            }
            size="small"
            onChange={(e) => {
              setSelectedSlotDefinition(e.target.value);
              console.log("Selected slot definition:", e.target.value);
            }}
          >
            {slotDefinitionsList.map((slotDef) => (
              <MenuItem
                key={slotDef.idslotDefinitionTable}
                value={slotDef.idslotDefinitionTable}
              >
                {slotDef.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button
          variant="contained"
          size="small"
          disabled={selectedRows.length === 0 || isLoadingSlots}
          sx={{
            color: "white !important",
            transition: "all 0.3s ease",
            "&:hover": {
              transform: "translateY(-2px)",
              boxShadow: 4,
            },
            "&.Mui-disabled": {
              backgroundColor: "rgba(0, 0, 0, 0.26) !important",
              color: "rgba(255, 255, 255, 0.5) !important",
            },
          }}
          onClick={handleLoadSlotDefinitionClick}
        >
          {isLoadingSlots
            ? t("common.saving") || "Kaydediliyor..."
            : t("stocks.nodeSlotGrid.loadSlotDefinition") ||
              "Slot Tanımını Yükle"}
        </Button>

        <Divider orientation="vertical" flexItem sx={{ display: { xs: "none", sm: "block" } }} />

        <Button
          variant="contained"
          size="small"
          color="error"
          disabled={selectedRows.length === 0 || isRemovingSlots}
          onClick={handleRemoveSlotDefinitionClick}
          sx={{ color: "white !important" }}
        >
          {isRemovingSlots
            ? t("common.deleting") || "Siliniyor..."
            : t("stocks.nodeSlotGrid.removeSlotDefinition") ||
              "Slot Tanımını Kaldır"}
        </Button>
      </Box>

      <Box sx={{ flexGrow: 1 }}>
        <DataGrid
          rows={rows}
          columns={getColumns(t, onlineIds, columnFilters, setColumnFilters)}
          getRowId={(row) => row.idNode}
          loading={loading}
          checkboxSelection
          disableRowSelectionOnClick
          onRowSelectionModelChange={(newSelection) => {
            setSelectedRows(newSelection);
            console.log("Selected rows:", newSelection);
          }}
          rowSelectionModel={selectedRows}
          sx={{
            "& .MuiDataGrid-columnHeader": {
              minHeight: "120px !important",
              maxHeight: "120px !important",
            },
            "& .MuiDataGrid-columnHeaderTitle": {
              overflow: "visible",
              lineHeight: "normal",
              whiteSpace: "normal",
            },
          }}
        />
      </Box>

      {/* Load Confirmation Dialog */}
      <Dialog
        open={openLoadConfirmDialog}
        onClose={() => setOpenLoadConfirmDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t("stocks.nodeSlotGrid.loadConfirmTitle")}</DialogTitle>
        <DialogContent>
          <Typography>
            {t("stocks.nodeSlotGrid.loadConfirmMessage", {
              count: selectedRows.length,
            })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenLoadConfirmDialog(false)}>
            {t("common.cancel")}
          </Button>
          <Button
            onClick={handleLoadSlotDefinitionConfirm}
            variant="contained"
            color="primary"
          >
            {t("common.ok")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Remove Confirmation Dialog */}
      <Dialog
        open={openRemoveConfirmDialog}
        onClose={() => setOpenRemoveConfirmDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t("stocks.nodeSlotGrid.removeConfirmTitle")}</DialogTitle>
        <DialogContent>
          <Typography>
            {t("stocks.nodeSlotGrid.removeConfirmMessage", {
              count: selectedRows.length,
            })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenRemoveConfirmDialog(false)}>
            {t("common.cancel")}
          </Button>
          <Button
            onClick={handleRemoveSlotDefinitionConfirm}
            variant="contained"
            color="error"
          >
            {t("common.ok")}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={toast.open}
        autoHideDuration={6000}
        onClose={() => setToast({ ...toast, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setToast({ ...toast, open: false })}
          severity={toast.severity}
          sx={{ width: "100%" }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
